/**
 * Phase 124E — Super Admin Demo Capture Utility (frontend-only).
 *
 * Renders any public ArenaX academy surface (default: Wall of Champions)
 * into an off-screen, same-origin <iframe> at fixed viewport dimensions and
 * uses html2canvas to serialize it to a PNG that downloads directly to the
 * operator's machine.  Zero backend / zero storage — every render happens
 * in the browser, off the critical payment/webhook surface.
 *
 *   Desktop preset:  1920 x 1080
 *   Mobile preset:    390 x 844
 */

import { useRef, useState, useCallback } from 'react';
import html2canvas from 'html2canvas';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Camera, Download, Loader2, Monitor, Smartphone, Globe, Twitter, Linkedin, Mail, Check } from 'lucide-react';
import { toast } from 'sonner';

const PRESETS = {
  desktop: { label: 'Desktop',  width: 1920, height: 1080 },
  mobile:  { label: 'Mobile',   width:  390, height:  844 },
};

const sanitizeSlug = (raw) =>
  (raw || '').trim().toLowerCase().replace(/[^a-z0-9-_]/g, '').slice(0, 64);

// Phase 124F — Client-side social share intent helpers.
//
// The share URL is derived from `window.location.origin` so a capture taken
// from production naturally produces an out-play.in link, while preview
// captures point at the preview host.  No hardcoded domain assumptions.
const buildShareUrl = (slug) => `${window.location.origin}/academy/${slug}`;

// Phase 124G — Personalized copy when public-wall metadata is available.
// `meta` is the optional output of `fetchWallMeta()` below.  When absent
// (network fail, fresh slug not yet fetched), the Phase 124F fallback fires.
//
// Phase 124H — Spotlight selection moved to a single source (`champions[0]`).
// The backend already pin-sorts champions so the pinned player (if any) sits
// at index 0; otherwise index 0 is the top algorithmic ranker.  Empty roster
// quietly omits the spotlight line.
const buildShareText = (slug, meta) => {
  const url = buildShareUrl(slug);
  if (meta && meta.academy_name) {
    const lines = [
      `🚀 ${meta.academy_name} just crowned ${meta.total_champions} champion${meta.total_champions === 1 ? '' : 's'} on Out-Play 🏆`,
    ];
    if (meta.spotlight_player) {
      const milestoneTag = meta.spotlight_milestone ? ` — “${meta.spotlight_milestone}”` : '';
      lines.push(`\nSpotlight: ${meta.spotlight_player}${milestoneTag}`);
    }
    lines.push(`\nSee the wall ↓\n${url}`);
    return lines.join('');
  }
  return (
    `🚀 Spotlight on the newest Wall of Champions at ${slug} — ` +
    `powered by Out-Play, the operating system for India's sports academies.\n\n${url}`
  );
};

const buildIntent = (target, slug, meta) => {
  if (target === 'twitter') {
    return `https://twitter.com/intent/tweet?text=${encodeURIComponent(buildShareText(slug, meta))}`;
  }
  if (target === 'linkedin') {
    return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(buildShareUrl(slug))}`;
  }
  return null;
};

// Phase 124G — Best-effort public-wall metadata fetch.  Reads the existing
// `/api/public/academy/{slug}` payload (no new backend endpoint) and derives
// the four fields the personalized copy needs.  Returns `null` on any failure
// so the caller can transparently fall back to the Phase 124F template.
//
// Phase 124H — Spotlight is now `champions[0]` (the backend already pin-sorts
// so this is the pinned champion if one exists, otherwise the top algorithmic
// ranker).  `spotlight_source` indicates which path was used so the UI can
// surface it in the Personalized badge tooltip.
async function fetchWallMeta(slug) {
  try {
    const res = await fetch(`${window.location.origin}/api/public/academy/${slug}`, {
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return null;
    const data = await res.json();
    const champions = Array.isArray(data.champions) ? data.champions : [];
    const spotlight = champions[0] || null;
    let spotlightSource = 'none';
    if (spotlight) {
      spotlightSource = spotlight.is_pinned ? 'pinned' : 'top_ranked';
    }
    return {
      academy_name: (data.academy && data.academy.name) || '',
      total_champions: champions.length,
      spotlight_player: spotlight ? spotlight.name : '',
      spotlight_milestone: spotlight && spotlight.custom_milestone ? spotlight.custom_milestone : '',
      spotlight_source: spotlightSource,
    };
  } catch (_err) {
    return null;
  }
}

// Phase 124I — Composes a personalized B2B outreach email draft from the
// cached public-wall metadata.  Returns `{ subject, body }`.  When `meta` is
// absent (e.g. brand-new slug, network failure), emits a clean generic
// fallback that still references the slug and wall URL.
const compileOutreachEmail = (slug, meta) => {
  const wallUrl = buildShareUrl(slug);
  const academy = (meta && meta.academy_name) || slug;
  const count = meta ? meta.total_champions : null;
  const spotlight = meta && meta.spotlight_player ? meta.spotlight_player : '';
  const milestone = meta && meta.spotlight_milestone ? meta.spotlight_milestone : '';

  const subject = meta && meta.academy_name
    ? `${academy} — your Wall of Champions is live on Out-Play`
    : `Your Wall of Champions on Out-Play`;

  const lines = [];
  lines.push(`Hi ${academy} team,`);
  lines.push('');
  if (meta && count !== null) {
    lines.push(
      `Just pulled a fresh visual of your live Wall of Champions on Out-Play — ` +
      `${count} champion${count === 1 ? '' : 's'} currently spotlighted publicly.`
    );
  } else {
    lines.push(
      `Just pulled a fresh visual of your live Wall of Champions on Out-Play — ` +
      `ready to share with parents, scouts, and the wider community.`
    );
  }
  if (spotlight) {
    lines.push('');
    lines.push(`Lead spotlight: ${spotlight}${milestone ? ` — "${milestone}"` : ''}.`);
  }
  lines.push('');
  lines.push(`The page is live here: ${wallUrl}`);
  lines.push('');
  lines.push(
    `Attached you'll find the desktop (1920×1080) and mobile (390×844) renders ` +
    `we generated locally — both are ready to drop straight into your sales decks, ` +
    `social posts, or parent communications.`
  );
  lines.push('');
  lines.push(`Let me know if you'd like a walkthrough of the curation tools (Pin to Top, Custom Milestones) or how to bring more athletes onto the wall.`);
  lines.push('');
  lines.push(`Best,`);
  lines.push(`Out-Play Team`);

  return { subject, body: lines.join('\n') };
};

// Phase 124I — Single string formatted for paste into a fresh email compose
// window (subject + visual separator + body).
const formatClipboardDraft = (slug, meta) => {
  const { subject, body } = compileOutreachEmail(slug, meta);
  return `Subject: ${subject}\n\n${'='.repeat(40)}\n\n${body}`;
};

async function captureViewport({ url, width, height }) {
  // Off-screen iframe sized to the target viewport.  Same-origin means
  // html2canvas can walk its contentDocument freely.
  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('data-testid', 'demo-capture-iframe');
  iframe.style.position = 'fixed';
  iframe.style.top = '0';
  iframe.style.left = '-99999px';   // visually off-canvas
  iframe.style.width = `${width}px`;
  iframe.style.height = `${height}px`;
  iframe.style.border = '0';
  iframe.src = url;
  document.body.appendChild(iframe);

  try {
    await new Promise((resolve, reject) => {
      const timeout = setTimeout(
        () => reject(new Error('iframe load timed out after 15s')),
        15000,
      );
      iframe.onload = () => { clearTimeout(timeout); resolve(); };
      iframe.onerror = () => { clearTimeout(timeout); reject(new Error('iframe failed to load')); };
    });

    // Give images, fonts, and the Wall hero collage one extra paint cycle.
    await new Promise((r) => setTimeout(r, 1800));

    const doc = iframe.contentDocument;
    if (!doc || !doc.body) {
      throw new Error('iframe content not accessible (cross-origin?)');
    }

    const canvas = await html2canvas(doc.body, {
      width,
      height,
      windowWidth: width,
      windowHeight: height,
      useCORS: true,
      backgroundColor: '#ffffff',
      scale: 1,
      logging: false,
    });

    return await new Promise((resolve, reject) => {
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new Error('PNG blob conversion failed'))),
        'image/png',
        0.95,
      );
    });
  } finally {
    iframe.remove();
  }
}

function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after the click handler has fully drained.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

export default function DemoCaptureCard() {
  const [slug, setSlug]   = useState('test1-academy');
  const [busy, setBusy]   = useState(null);    // 'desktop' | 'mobile' | 'both' | null
  const [last, setLast]   = useState(null);    // { label, at } of last successful capture
  // Phase 124G — Personalization metadata fetched alongside capture.  `slug`
  // is stored so we know whether the cache is stale relative to the input.
  const [meta, setMeta]   = useState(null);    // { academy_name, total_champions, spotlight_player, spotlight_milestone, spotlight_source, slug } | null
  // Phase 124I — transient "copied" state for the outreach-draft clipboard write
  const [copied, setCopied] = useState(false);
  const abortRef = useRef(false);

  const run = useCallback(async (mode) => {
    const clean = sanitizeSlug(slug);
    if (!clean) {
      toast.error('Enter a valid academy slug (letters, numbers, hyphens).');
      return;
    }
    abortRef.current = false;
    setBusy(mode);
    const origin = window.location.origin;
    const targetUrl = `${origin}/academy/${clean}`;
    const today = new Date().toISOString().slice(0, 10);

    try {
      const presets = mode === 'both'
        ? [['desktop', PRESETS.desktop], ['mobile', PRESETS.mobile]]
        : [[mode, PRESETS[mode]]];

      for (const [key, p] of presets) {
        if (abortRef.current) break;
        toast.message(`Capturing ${p.label.toLowerCase()} (${p.width}×${p.height})…`);
        const blob = await captureViewport({ url: targetUrl, width: p.width, height: p.height });
        downloadBlob(blob, `${clean}-wall-${key}-${today}.png`);
      }

      setLast({
        label: mode === 'both' ? 'Desktop + Mobile' : PRESETS[mode].label,
        at: new Date().toLocaleString(),
      });
      toast.success('Capture complete — PNG downloaded to your machine.');

      // Phase 124G — Fire-and-forget metadata fetch in parallel with the toast.
      // Failure is silent: the share button click handler falls back to the
      // generic Phase 124F template if `meta` is still null.
      fetchWallMeta(clean).then((m) => {
        if (m) setMeta({ ...m, slug: clean });
      });
    } catch (err) {
      const msg = err?.message || 'Capture failed';
      toast.error(`Capture failed: ${msg}`);
    } finally {
      setBusy(null);
    }
  }, [slug]);

  const disabled = busy !== null;
  const slugClean = sanitizeSlug(slug);
  const shareReady = Boolean(slugClean && last);
  // Personalization is "live" only when the cached metadata matches the
  // current sanitized slug (operator may have edited the slug after capture).
  const personalized = Boolean(meta && meta.slug === slugClean);

  const openShare = useCallback((target) => {
    if (!slugClean || !last) return;
    const effectiveMeta = (meta && meta.slug === slugClean) ? meta : null;
    const intentUrl = buildIntent(target, slugClean, effectiveMeta);
    if (!intentUrl) return;
    window.open(intentUrl, '_blank', 'noopener,noreferrer');
    toast.message(`Opening ${target === 'twitter' ? 'X' : 'LinkedIn'} share dialog…`);
  }, [slugClean, last, meta]);

  // Phase 124I — Composes the outreach email and writes it to the clipboard
  // inside a fresh user-gesture click handler, which is the only reliable way
  // to satisfy modern browser clipboard policy.  `navigator.clipboard` is
  // unavailable on insecure origins; fall back to a textarea-based copy so
  // older preview environments still work.
  const copyOutreachDraft = useCallback(async () => {
    if (!slugClean || !last) return;
    const effectiveMeta = (meta && meta.slug === slugClean) ? meta : null;
    const text = formatClipboardDraft(slugClean, effectiveMeta);
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        ok = true;
      } else {
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.left = '-99999px';
        document.body.appendChild(ta);
        ta.select();
        ok = document.execCommand && document.execCommand('copy');
        ta.remove();
      }
    } catch (_err) {
      ok = false;
    }
    if (ok) {
      setCopied(true);
      toast.success('Outreach email draft copied to clipboard.');
      setTimeout(() => setCopied(false), 2500);
    } else {
      toast.error('Clipboard write failed — your browser blocked it.');
    }
  }, [slugClean, last, meta]);

  return (
    <Card data-testid="demo-capture-card">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center gap-2">
          <Camera className="h-4 w-4 text-primary" />
          Demo Screenshot Capture
        </CardTitle>
        <p className="text-xs text-muted-foreground">
          Generate sales-ready PNGs of any public academy Wall of Champions —
          desktop (1920×1080) + mobile (390×844). Renders client-side, downloads
          directly to your machine. No server storage touched.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="demo-capture-slug" className="text-xs">
            Academy slug
          </Label>
          <div className="relative">
            <Globe className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="demo-capture-slug"
              data-testid="demo-capture-slug-input"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="test1-academy"
              className="h-9 pl-8 text-sm"
              disabled={disabled}
              maxLength={64}
            />
          </div>
          <p className="text-[11px] text-muted-foreground">
            Target: <span className="font-mono">/academy/{sanitizeSlug(slug) || '—'}</span>
          </p>
        </div>

        <div className="grid sm:grid-cols-3 gap-2">
          <Button
            variant="outline"
            size="sm"
            className="h-9 justify-start gap-2"
            onClick={() => run('desktop')}
            disabled={disabled || !sanitizeSlug(slug)}
            data-testid="demo-capture-desktop-btn"
          >
            {busy === 'desktop'
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Monitor className="h-3.5 w-3.5" />}
            Desktop · 1920
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-9 justify-start gap-2"
            onClick={() => run('mobile')}
            disabled={disabled || !sanitizeSlug(slug)}
            data-testid="demo-capture-mobile-btn"
          >
            {busy === 'mobile'
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Smartphone className="h-3.5 w-3.5" />}
            Mobile · 390
          </Button>
          <Button
            size="sm"
            className="h-9 justify-start gap-2 bg-primary"
            onClick={() => run('both')}
            disabled={disabled || !sanitizeSlug(slug)}
            data-testid="demo-capture-both-btn"
          >
            {busy === 'both'
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <Download className="h-3.5 w-3.5" />}
            Capture Both
          </Button>
        </div>

        {last && (
          <p className="text-[11px] text-muted-foreground" data-testid="demo-capture-last">
            Last capture: <span className="font-medium text-foreground">{last.label}</span> · {last.at}
          </p>
        )}

        {/* Phase 124F — One-click social share intents (client-side only) */}
        <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-border/50 mt-2"
             data-testid="demo-share-row">
          <span className="text-[11px] text-muted-foreground mr-1">Amplify:</span>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            disabled={!shareReady}
            onClick={() => openShare('twitter')}
            data-testid="demo-share-x-btn"
            title={shareReady ? 'Share on X with pre-filled copy' : 'Capture first to enable sharing'}
          >
            <Twitter className="h-3.5 w-3.5" /> Share on X
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-8 gap-1.5 text-xs"
            disabled={!shareReady}
            onClick={() => openShare('linkedin')}
            data-testid="demo-share-linkedin-btn"
            title={shareReady ? 'Share on LinkedIn' : 'Capture first to enable sharing'}
          >
            <Linkedin className="h-3.5 w-3.5" /> Share on LinkedIn
          </Button>
          {/* Phase 124I — One-click B2B outreach email draft to clipboard */}
          <Button
            variant="outline"
            size="sm"
            className={`h-8 gap-1.5 text-xs ${copied ? 'border-emerald-300 text-emerald-700' : ''}`}
            disabled={!shareReady}
            onClick={copyOutreachDraft}
            data-testid="demo-copy-email-btn"
            title={shareReady ? 'Copy a personalized B2B outreach email draft to your clipboard' : 'Capture first to enable email draft'}
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Mail className="h-3.5 w-3.5" />}
            {copied ? 'Copied!' : 'Copy Email Draft'}
          </Button>
          {/* Phase 124G — surface whether the click will emit personalized vs generic copy */}
          {/* Phase 124H — tooltip now reflects whether the spotlight is pinned vs algorithmic top-ranked */}
          {shareReady && personalized && (
            <span
              className="text-[10px] uppercase tracking-wide font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
              data-testid="demo-share-personalized-badge"
              title={
                `Tailored to ${meta.academy_name} · ${meta.total_champions} champion${meta.total_champions === 1 ? '' : 's'}` +
                (meta.spotlight_player
                  ? ` · Spotlight: ${meta.spotlight_player}` +
                    (meta.spotlight_source === 'top_ranked' ? ' (top-ranked)' : ' (pinned)')
                  : '')
              }
            >
              Personalized
            </span>
          )}
          {!shareReady && (
            <span className="text-[11px] text-muted-foreground" data-testid="demo-share-hint">
              {slugClean ? 'Capture a PNG first to enable sharing.' : 'Enter a slug + capture to share.'}
            </span>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
